import { DataTestId } from '@mews-commander/enums';
import { LocalizedText, LocalizedTextProps } from '@mews/framework';
import {
    Body,
    BodyProps,
    Highlight,
    HighlightProps,
    Title,
    TitleProps,
    Typography,
    TypographyComponentProps,
    TypographyProps,
} from '@optimus-web/core';

interface Props {
    testId?: DataTestId;
}

type TitlePropsWithoutLevel = Omit<TitleProps, 'level'>;

function localizedTypographyFactory<T>(TypographyComponent: React.ComponentType<T>) {
    const WrappedLocalizedText: React.FC<React.PropsWithChildren<Props & LocalizedTextProps & T>> = ({
        children,
        firstLowerCase,
        lowerCase,
        substitutes,
        testId,
        textKey,
        upperCase,
        ...other
    }) => (
        <TypographyComponent data-test-id={testId} {...(other as T)}>
            <LocalizedText
                firstLowerCase={firstLowerCase}
                lowerCase={lowerCase}
                substitutes={substitutes}
                textKey={textKey}
                upperCase={upperCase}
            />
        </TypographyComponent>
    );

    return WrappedLocalizedText;
}

export const LocalizedPageTitle = localizedTypographyFactory<TitlePropsWithoutLevel>((props) => (
    <Title {...props} level={1} />
));
export const LocalizedParagraph = localizedTypographyFactory<BodyProps>((props) => <Body {...props} />);
export const LocalizedSmallParagraph = localizedTypographyFactory<BodyProps>((props) => (
    <Body size="small" {...props} />
));
export const LocalizedLabel = localizedTypographyFactory<TypographyComponentProps>((props) => (
    <Typography textStyle="bodyMediumStrong" {...props} />
));
export const LocalizedSmallLabel = localizedTypographyFactory<TypographyComponentProps>((props) => (
    <Typography textStyle="bodySmallStrong" {...props} />
));
export const LocalizedSectionTitle = localizedTypographyFactory<TitlePropsWithoutLevel>((props) => (
    <Title {...props} level={2} />
));
export const LocalizedSubsectionTitle = localizedTypographyFactory<TitlePropsWithoutLevel>((props) => (
    <Title {...props} level={3} />
));
export const LocalizedMinorHighlight = localizedTypographyFactory<HighlightProps>((props) => (
    <Highlight size="small" {...props} />
));
export const LocalizedModerateHighlight = localizedTypographyFactory<HighlightProps>((props) => (
    <Highlight size="medium" {...props} />
));
export const LocalizedMajorHighlight = localizedTypographyFactory<HighlightProps>((props) => (
    <Highlight size="large" {...props} />
));
export const LocalizedTypography = localizedTypographyFactory<TypographyProps>((props) => <Typography {...props} />);
